import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ParsedMessage } from '../lib/parser';
import { mimeForMember, type MemberMeta } from '../lib/archive';
import { mimeForFilename as guessMime } from '../lib/media';
import { ownerOf } from '../lib/names';
import { openDocumentNatively } from '../lib/native';

// The prototype put 33,171 rows in the DOM and crawled. This keeps roughly
// thirty, whatever the chat length.

const PALETTE = ['#e5786d', '#5f9ea0', '#b07d62', '#7b8fbf', '#8a9a5b', '#b0729b', '#6f8faf'];
const colourFor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
};

const OMITTED_LABEL: Record<string, string> = {
  image: 'Photo', video: 'Video', audio: 'Voice message', sticker: 'Sticker',
  gif: 'GIF', document: 'Document', media: 'Attachment',
};

const dayOf = (ts: number) => new Date(ts).toISOString().slice(0, 10);
const time = (ts: number) => new Date(ts).toISOString().slice(11, 16);

type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'msg'; key: string; m: ParsedMessage };;

export function ChatView({ messages, manifest, mimeFor, focusSeq, resolve, myName, title }: {
  messages: ParsedMessage[];
  manifest: Record<string, MemberMeta>;
  mimeFor: (kind: number) => string;
  /** The account's own name. An export labels your messages with it, so it
   *  is what puts them on the right — see ownerOf. */
  myName?: string | null;
  /** What this conversation is called, which for a chat with one other
   *  person is their name and therefore not yours. */
  title?: string;
  /** A search hit to land on, instead of the usual jump to the newest message. */
  focusSeq?: number;
  /** Preview only: pull an attachment out of the zip on demand. */
  resolve?: (name: string) => Promise<string | null>;
}) {
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    let day = '';
    for (const m of messages) {
      const d = dayOf(m.ts);
      if (d !== day) { day = d; out.push({ kind: 'day', key: `d${d}`, label: d }); }
      out.push({ kind: 'msg', key: `m${m.seq}`, m });
    }
    return out;
  }, [messages]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => (rows[i]!.kind === 'day' ? 40 : 64),
    overscan: 12,
  });

  const [flash, setFlash] = useState<number | null>(null);

  // A search hit wins over the usual jump to the newest message, and only
  // while it is fresh — scrolling away and back should not drag you off the
  // bottom again, so the seq is consumed rather than watched.
  useEffect(() => {
    if (focusSeq === undefined) {
      virt.scrollToIndex(rows.length - 1, { align: 'end' });
      return;
    }
    const i = rows.findIndex((r) => r.kind === 'msg' && r.m.seq === focusSeq);
    if (i < 0) { virt.scrollToIndex(rows.length - 1, { align: 'end' }); return; }
    // Measuring a virtualised list is asynchronous: the first scroll lands
    // close, the second lands on it once the rows around it have real heights.
    virt.scrollToIndex(i, { align: 'center' });
    const again = setTimeout(() => virt.scrollToIndex(i, { align: 'center' }), 120);
    setFlash(focusSeq);
    const off = setTimeout(() => setFlash(null), 2200);
    return () => { clearTimeout(again); clearTimeout(off); };
  }, [rows.length, focusSeq]);

  // Whoever spoke first used to be treated as the owner. That is not a
  // weak signal, it is no signal: in any conversation somebody else opened
  // — most of them — it puts their messages on the right and yours on the
  // left, backwards from every chat app there is.
  const owner = useMemo(() => ownerOf(messages, myName, title), [messages, myName, title]);

  const [doc, setDoc] = useState<DocState | null>(null);

  /**
   * Buscar os bytes aqui, em vez de navegar para a URL deles.
   *
   * Navegar era o que estava: um `<a href="/media/…">` sem `target`. O PDF
   * substituía a aplicação — nada atrás, nada para voltar — e dependia de o
   * Service Worker interceptar uma *navegação*, que num WKWebView não é de
   * confiança. Quando não interceptava, o pedido chegava ao Hosting, que
   * reescreve tudo para `/index.html`: HTTP 200, `text/html`, e o próprio
   * app a aparecer onde devia estar o documento. Guardar aquilo dava um
   * ficheiro `.pdf` que era uma página web.
   *
   * Um `fetch` é um pedido normal, do tipo que o worker já serve todos os
   * dias para cada imagem da conversa. E tendo os bytes na mão, abrir deixa
   * de precisar de uma aba nova.
   */
  const openDoc = useCallback(async (url: string, name: string, mime: string) => {
    setDoc({ phase: 'loading', name });
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      // Um HTML aqui nunca é o ficheiro — é o `index.html` do Hosting a
      // fazer-se passar por ele, com 200 e tudo.
      if (/^text\/html/i.test(res.headers.get('content-type') ?? '')) {
        throw new Error('the app came back instead of the file');
      }
      // O tipo vem da resposta, não do manifesto.
      //
      // O manifesto guarda o género grosso do membro, e para um documento
      // isso é `application/octet-stream` — com o qual nada se
      // pré-visualiza. O worker já sabe melhor: recebe o nome na URL e
      // deriva dele o tipo verdadeiro. Era preciso perguntar-lhe.
      const served = (res.headers.get('content-type') ?? '').split(';')[0]!.trim();
      const real = served && served !== 'application/octet-stream' ? served : mime;
      const body = new Uint8Array(await res.arrayBuffer());
      // No app, o visualizador do sistema: abre por cima e já traz partilha.
      // Devolve falso se não houver casca, ou se o ficheiro for grande
      // demais para a ponte — e aí cai na folha, como no navegador.
      if (openDocumentNatively(name, real, body)) { setDoc(null); return; }
      setDoc({
        phase: 'ready', name, mime: real,
        url: URL.createObjectURL(new Blob([body as BlobPart], { type: real })),
        size: body.length,
      });
    } catch (e) {
      setDoc({ phase: 'failed', name, why: e instanceof Error ? e.message : 'unknown' });
    }
  }, []);

  const closeDoc = useCallback(() => {
    setDoc((d) => { if (d?.phase === 'ready') URL.revokeObjectURL(d.url); return null; });
  }, []);

  return (
    <div className="chat" ref={parentRef}>
      <div className="spacer" style={{ height: virt.getTotalSize() }}>
        {virt.getVirtualItems().map((v) => {
          const row = rows[v.index]!;
          return (
            <div key={row.key}
                 className={`vrow${row.kind === 'msg' && row.m.seq === flash ? ' flash' : ''}`}
                 ref={virt.measureElement} data-index={v.index}
                 style={{ transform: `translateY(${v.start}px)` }}>
              {row.kind === 'day'
                ? <div className="day"><span>{row.label}</span></div>
                : <Bubble m={row.m} mine={row.m.sender === owner} resolve={resolve}
                          manifest={manifest} mimeFor={mimeFor} onOpenDoc={openDoc} />}
            </div>
          );
        })}
      </div>
      {doc && <DocSheet doc={doc} onClose={closeDoc} />}
    </div>
  );
}

type DocState =
  | { phase: 'loading'; name: string }
  | { phase: 'ready'; name: string; mime: string; url: string; size: number }
  | { phase: 'failed'; name: string; why: string };

/**
 * O documento por cima da conversa, que continua ali atrás.
 *
 * Só no navegador: dentro do app isto nunca se monta, porque o QuickLook
 * ficou com o trabalho. Um `<iframe>` com um PDF desenha-se no Chrome e no
 * Safari do computador; num WKWebView não se pode contar com ele, e é por
 * isso que lá se vai pelo sistema.
 */
function DocSheet({ doc, onClose }: { doc: DocState; onClose: () => void }) {
  // Escape fecha, como em qualquer coisa que tape o ecrã.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose]);

  const previewable = doc.phase === 'ready'
    && (doc.mime === 'application/pdf' || doc.mime.startsWith('image/')
      || doc.mime.startsWith('text/'));

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={doc.name}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet-body doc-sheet">
        <header>
          <h2>{doc.name}</h2>
          <button className="x" onClick={onClose} aria-label="Close">×</button>
        </header>

        {doc.phase === 'loading' && <p className="fine">Decrypting…</p>}

        {doc.phase === 'failed' && (
          // Dizer o que falhou, em vez de entregar um ficheiro errado com o
          // nome certo — que era o comportamento antigo.
          <>
            <p className="fine">This file could not be read: {doc.why}.</p>
            <p className="fine">If this chat was imported a while ago, importing
              it again is what fixes it.</p>
          </>
        )}

        {doc.phase === 'ready' && (
          <>
            {previewable
              ? <iframe className="doc-frame" src={doc.url} title={doc.name} />
              : <p className="fine">{bytes(doc.size)} · no preview for this kind
                  of file. Save it to open it.</p>}
            <div className="doc-acts">
              {/* Um `download` com um blob: nada do servidor no caminho, e
                  portanto nada que o Hosting possa reescrever. */}
              <a className="go" href={doc.url} download={doc.name}>Save</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * In a preview the src is not a URL yet — it is a file inside a zip on disk.
 * Resolving it here rather than up front is what keeps a 3.5 GB export cheap:
 * the list is virtualised, so only the rows on screen ever hold a blob.
 */
function useResolved(attach: string | undefined, resolve?: (n: string) => Promise<string | null>) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!attach || !resolve) return;
    let live = true;
    void resolve(attach).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [attach, resolve]);
  return url;
}

/** Asks the server what a member is, for a browser that did not import it. */
function useMemberMime(attach: string | undefined) {
  const [mime, setMime] = useState<string | null>(null);
  useEffect(() => {
    if (!attach) return;
    let live = true;
    void mimeForMember(attach).then((t) => { if (live) setMime(t); }).catch(() => {});
    return () => { live = false; };
  }, [attach]);
  return mime;
}

/** "PDF", "DOCX" — o que vai no quadrado, vindo do nome ou do tipo. */
function extOf(name: string | undefined, mime: string): string {
  const fromName = name?.includes('.') ? name.split('.').pop()! : '';
  const fromMime = mime.split('/').pop()?.replace('vnd.openxmlformats-officedocument.', '') ?? '';
  const ext = (fromName || fromMime).slice(0, 4);
  return ext ? ext.toUpperCase() : 'FILE';
}

/** Tamanho como uma pessoa o lê, e nada quando não se sabe. */
function bytes(n: number | undefined): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Bubble({ m, mine, manifest, mimeFor, resolve, onOpenDoc }: {
  m: ParsedMessage; mine: boolean;
  manifest: Record<string, MemberMeta>; mimeFor: (k: number) => string;
  resolve?: (name: string) => Promise<string | null>;
  /** Abre o documento sem sair da conversa. */
  onOpenDoc?: (url: string, name: string, mime: string) => void;
}) {
  // A preview has no content ids — ingest assigns those. The parser gives the
  // filename as written in the transcript, and that is the key into the zip.
  const attachKey = resolve ? m.file : m.attach;
  const local = useResolved(m.kind === 1 ? undefined : attachKey, resolve);
  if (m.kind === 1) return <div className="system">{m.body}</div>;

  const meta = m.attach ? manifest[m.attach] : undefined;
  // Preview: no manifest, so the extension is all there is to go on.
  // The manifest belongs to the browser that did the import; on any other
  // device it is empty, and an empty type rendered every photo as a generic
  // "attachment" link. The transcript's filename is not a fallback — it is
  // deleted when the chunk is written — so the server is asked instead, once
  // per member, and only for rows actually on screen.
  const asked = useMemberMime(resolve ? undefined : (meta?.mime ? undefined : m.attach));
  const mime = resolve
    ? guessMime(m.file ?? '')
    : (meta?.mime || asked || '');
  const src = resolve ? local : (m.attach ? `/media/${m.attach}` : null);
  if (resolve && attachKey && !src) {
    return (
      <div className={`bubble ${mine ? 'mine' : 'theirs'}`}>
        {!mine && m.sender && (
          <div className="who" style={{ color: colourFor(m.sender) }}>{m.sender}</div>
        )}
        <div className="media pending" />
        {m.body && <div className="text">{m.body}</div>}
        <time>{time(m.ts)}</time>
      </div>
    );
  }
  return (
    <div className={`bubble ${mine ? 'mine' : 'theirs'}`}>
      {!mine && m.sender && (
        <div className="who" style={{ color: colourFor(m.sender) }}>{m.sender}</div>
      )}
      {src && mime.startsWith('image/') && (
        // Served by the Service Worker, which decrypts it, or straight from the
        // zip in a preview. The tag knows nothing about either.
        <img className="media" src={src} alt="" loading="lazy" />
      )}
      {src && mime.startsWith('video/') && (
        <video className="media" src={src} controls preload="metadata" />
      )}
      {src && mime.startsWith('audio/') && (
        <audio src={src} controls preload="none" />
      )}
      {src && !mime.startsWith('image/') && !mime.startsWith('video/') && !mime.startsWith('audio/') && (
        // Um documento é o seu nome. Era um link a dizer "attachment", e
        // quem recebia um orçamento e um contrato no mesmo dia não tinha
        // como saber qual era qual sem abrir os dois.
        //
        // Um botão, não um link: tocar já não navega para o ficheiro. O nome
        // continua a viajar na URL, porque é dele que o worker tira o tipo
        // certo — sem ele um PDF chega como `octet-stream`.
        <button className="doc" type="button"
                onClick={() => onOpenDoc?.(
                  m.name && !resolve ? `${src}?name=${encodeURIComponent(m.name)}` : src,
                  m.name ?? 'Attachment', mime)}>
          <span className="doc-kind" aria-hidden="true">{extOf(m.name, mime)}</span>
          <span className="doc-what">
            <strong>{m.name ?? 'Attachment'}</strong>
            <small>{[bytes(meta?.plainLength), extOf(m.name, mime).toLowerCase()]
              .filter(Boolean).join(' · ')}</small>
          </span>
        </button>
      )}
      {m.omitted && (
        // The file was never in the export. Naming what is missing, and why,
        // beats showing WhatsApp's own marker as ordinary message text.
        <div className="omitted">
          <span>{OMITTED_LABEL[m.omitted] ?? 'Attachment'}</span>
          <small>not included in this export</small>
        </div>
      )}
      {m.body && <div className="text">{m.body}</div>}
      <time>{time(m.ts)}</time>
    </div>
  );
}
